/**
 * The rules behind the outbound email queue screen.
 *
 * Named for the concern rather than the component, like lifecycleRules.ts
 * beside LifecyclePanel.tsx and sequenceRules.ts beside Sequences.tsx.
 *
 * WHY THE RULES ARE HERE AND NOT IN THE COMPONENT
 * -----------------------------------------------
 * The `marketing` vitest project globs plain `.ts` and runs in Node; `app`
 * runs in real Chromium and costs a ~130 MB Playwright download. Everything
 * below either decides what is WRITTEN to `outbound_policy` -- the one row in
 * this system that can stop an irreversible send -- or decides what a person
 * is TOLD about why nothing is going out. None of it needs a DOM, and a
 * money-and-reputation control should not sit one browser install away from
 * being skipped. Same argument sendClient.ts makes about the batch loop.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHAT THIS FILE IS FORBIDDEN TO DO
 * ══════════════════════════════════════════════════════════════════════════
 *
 * IT DOES NOT DECIDE WHY A MESSAGE IS WAITING. `public.outbound_queue`
 * computes `wait_state` in SQL:
 *
 *     when claimed_at is not null then 'sending'
 *     when send_after  > now()    then 'held'
 *     when p.paused               then 'paused'
 *     when p.tripped_at not null  then 'stopped'
 *     else                             'due'
 *
 * and 067 created that column precisely because "held" and "due" are
 * different facts that a screen merging them cannot explain. A second opinion
 * computed here from `send_after` and a browser clock is how this screen and
 * the drainer come to disagree about the same row: a laptop a few minutes
 * fast would show "due" over messages the database is still holding, and
 * somebody would cancel them believing they were about to go out.
 *
 * So every function here FORMATS what the view already decided. The same
 * discipline FollowUps.tsx's `lateness()` keeps about `is_stale`, and the
 * same reason: there is exactly one definition of each fact in the system.
 * ══════════════════════════════════════════════════════════════════════════
 */

import type { Invalidator } from "./touchActions";

/* ── the shapes 067 actually produces ──────────────────────────────────── */

/**
 * The five values of `outbound_queue.wait_state`, transcribed from the CASE
 * in migration 067. Typed as a union so a sixth one added in SQL and not here
 * fails the typecheck at every call site rather than rendering as a blank.
 */
export type WaitState = "sending" | "held" | "paused" | "stopped" | "due";

/**
 * One row of `public.outbound_queue` — everything still waiting, and why.
 *
 * `wait_state` is typed as the union WIDENED WITH string, deliberately. The
 * view is the authority and it can grow a state we have not been told about;
 * a narrow type would let TypeScript promise a completeness the database has
 * not agreed to, and the row would then fall through every branch silently.
 * describeWait() handles the unknown case out loud instead.
 */
export type OutboundQueueRow = {
  id: number;
  contact_id: number | null;
  first_name: string | null;
  last_name: string | null;
  subject: string | null;
  status: string;
  send_after: string;
  /** Set means IN FLIGHT, and past every control on this screen. */
  claimed_at: string | null;
  attempts: number;
  last_error: string | null;
  created_at: string;
  wait_state: WaitState | (string & {});
  /**
   * `greatest(send_after - now(), interval '0')`, so never negative. Arrives
   * as Postgres' own interval text — see intervalSeconds().
   */
  releases_in: string | null;
};

/** The single row of `public.outbound_policy`, id = 1. */
export type OutboundPolicy = {
  id: number;
  paused: boolean;
  pause_reason: string | null;
  hold_minutes: number;
  max_per_run: number;
  max_per_hour: number;
  tripped_at: string | null;
  tripped_reason: string | null;
  updated_at: string;
};

/* ── keeping the screen honest after a write ───────────────────────────── */

/**
 * What a pause, a resume, a settings change or a cancel moves.
 *
 * `emails` is here because cancel_outbound() writes it — the queue view reads
 * through to it, and a screen that adds a resource it does not write is one
 * nobody can reason about the next time something IS missing.
 *
 * `engagements` is deliberately ABSENT even though a trigger fires on
 * `emails.status`. private.sync_email_engagement() only inserts for
 * 'sent'/'delivered', and its delete branch has nothing to delete for a row
 * that was never sent — so a cancel provably cannot move that table. Same
 * reasoning that keeps `tasks` out of LIFECYCLE_INVALIDATES.
 */
export const OUTBOUND_INVALIDATES = [
  "outbound_queue",
  "outbound_policy",
  "emails",
] as const;

/**
 * Ask react-query to refetch everything an outbound write changed.
 *
 * A FUNCTION RATHER THAN THREE LINES IN A CLICK HANDLER, for the reason
 * invalidateTouched and invalidateLifecycle were extracted: the browser test
 * cannot prove those lines exist. Playwright's clicks fire window focus
 * events and react-query refetches on focus by default, so the list refreshes
 * for a reason that has nothing to do with our code and does not happen to a
 * person sitting still on a page. Deleting the invalidation entirely still
 * passes there.
 */
export function invalidateOutbound(client: Invalidator): Promise<unknown> {
  return Promise.all(
    OUTBOUND_INVALIDATES.map((resource) =>
      client.invalidateQueries({ queryKey: [resource] }),
    ),
  );
}

/* ── the interval the view hands us ────────────────────────────────────── */

/**
 * Postgres' interval text, as seconds.
 *
 * PostgREST serializes `interval` with to_json(), which is the server's own
 * text form under the default `intervalstyle`. Verified against this
 * database, not assumed:
 *
 *     00:04:59.5        1 day 02:03:04        00:00:00
 *
 * It is NOT a number and NOT ISO-8601, so `Number(releases_in)` is NaN and
 * `parseFloat(releases_in)` is 0 for every row — which would print "eligible
 * now" over the entire queue, including messages still inside their unsend
 * window. That is the mistake this function exists to make impossible.
 *
 * RETURNS NULL RATHER THAN GUESSING. `intervalstyle` is a database setting:
 * flip it to iso_8601 and every value becomes `PT4M59.5S`. A parser that fell
 * back to 0 would answer confidently and wrongly, and nothing anywhere would
 * say so. Null means the caller shows the absolute `send_after` instead.
 */
export function intervalSeconds(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const text = raw.trim();
  if (!text) return null;

  const match =
    /^(-?)(?:(\d+)\s+years?\s*)?(?:(\d+)\s+mons?\s*)?(?:(\d+)\s+days?\s*)?(?:(-?\d+):(\d{2}):(\d{2}(?:\.\d+)?))?$/.exec(
      text,
    );
  if (!match) return null;

  const [, sign, years, months, days, hours, minutes, seconds] = match;
  // A bare sign, or a string of separators, matches the regex with every
  // group undefined. That is not "zero", it is "nothing recognizable".
  if (![years, months, days, hours].some((part) => part != null)) return null;

  /*
   * A month is not a fixed number of seconds, and neither is a year. These
   * two branches exist only so a value carrying them is not silently read as
   * the time part alone — `releases_in` is a hold window measured in minutes,
   * so reaching them at all means something is very wrong. 30 and 365 are the
   * conventional approximations and the caller's wording ("at least") stays
   * true under both.
   */
  const total =
    Number(years ?? 0) * 365 * 86400 +
    Number(months ?? 0) * 30 * 86400 +
    Number(days ?? 0) * 86400 +
    Number(hours ?? 0) * 3600 +
    Number(minutes ?? 0) * 60 +
    Number(seconds ?? 0);

  return sign === "-" ? -total : total;
}

/**
 * How long a wait is, in words, ALWAYS ROUNDED DOWN.
 *
 * The sentence this feeds is of the form "at least N minutes away", and 4m59s
 * rounded up to "at least 5 minutes" is simply false. Down is the only
 * direction a floor can be rounded, and under a minute gets words rather than
 * "0 minutes", which reads as "now".
 */
function floorWait(seconds: number): string {
  if (seconds < 60) return "under a minute";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hourPart = `${hours} ${hours === 1 ? "hour" : "hours"}`;
  return rest === 0 ? hourPart : `${hourPart} ${rest} min`;
}

/**
 * When this row is released, in words — or null when we could not tell.
 *
 * Reads `releases_in`, which the view computed, rather than subtracting
 * `send_after` from a browser clock. See the banner at the top of this file.
 */
export function releaseText(
  row: Pick<OutboundQueueRow, "wait_state" | "releases_in">,
): string | null {
  /*
   * A CLAIMED ROW IS PAST EVERY CONTROL ON THIS SCREEN. cancel_outbound()
   * refuses it on purpose — the message may already be at Resend, and marking
   * it cancelled would make the log lie about something that went out. A
   * countdown beside it would suggest there is still time.
   */
  if (row.wait_state === "sending")
    return "already handed to the sender — too late to stop";

  const seconds = intervalSeconds(row.releases_in);
  if (seconds == null) return null;
  if (seconds <= 0) return "eligible now";
  return `at least ${floorWait(seconds)} away`;
}

/* ── what each wait_state means, and how loudly ────────────────────────── */

export type WaitTone = "normal" | "attention" | "alarm";

export type WaitDescription = {
  label: string;
  blurb: string;
  tone: WaitTone;
};

/**
 * The five states, in words, with how alarming each one is.
 *
 * `held` IS THE UNSEND WINDOW WORKING and reads as entirely normal. It will
 * be the state most rows are in, and dressing it as a warning would spend the
 * screen's alarm on the healthy case — after which `stopped`, the one state
 * that means a person must act, no longer stands out from it.
 *
 * `due` is normal too: it means the row is eligible and the next drainer run
 * will take it. A row that STAYS due is the interesting case, and the screen
 * says that in the standing note rather than by coloring every due row red.
 */
export const WAIT_STATES: Record<WaitState, WaitDescription> = {
  held: {
    label: "Held",
    blurb:
      "Inside the unsend window. This is the hold doing its job, and the only period in which anything can be cancelled.",
    tone: "normal",
  },
  due: {
    label: "Due",
    blurb:
      "Eligible now. The next drainer run takes it — and until that function is deployed, that run never comes.",
    tone: "normal",
  },
  sending: {
    label: "Sending",
    blurb:
      "Claimed by the drainer and possibly already at Resend. It cannot be cancelled: recording it as cancelled would make the log lie about a message that went out.",
    tone: "normal",
  },
  paused: {
    label: "Paused",
    blurb:
      "Due, but outbound email is paused, so nothing is draining. It will go out when somebody resumes.",
    tone: "attention",
  },
  stopped: {
    label: "Stopped",
    blurb:
      "Due, but the brake tripped and nothing is draining. A person has to clear the trip.",
    tone: "alarm",
  },
};

/**
 * One state, described — including one we have never heard of.
 *
 * The view owns `wait_state` and can grow a sixth value without this file
 * knowing. Rendering a blank for it would hide a queued message; saying the
 * word back and admitting we do not know it makes the gap findable.
 */
export function describeWait(state: string): WaitDescription {
  const known = WAIT_STATES[state as WaitState];
  if (known) return known;
  return {
    label: state,
    tone: "alarm",
    blurb: `public.outbound_queue reported the wait state "${state}", which this screen does not recognize. The view is the authority, so the row is real — but nothing here can tell you whether it is about to send.`,
  };
}

/* ── has anything actually been sent? ─────────────────────────────── */

/** One row of `public.pipeline_runs`, reduced to what this screen reads. */
export type DrainRun = {
  job: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  detail: string | null;
};

/**
 * The one job this screen is about.
 *
 * `pipeline_runs` IS SHARED BY FIVE JOBS. Counted 2026-09-05: nurture-tick
 * 25 rows, drain-outbound 20, ttb-refresh 5, nurture-digest 2, and
 * advance-enrollments, added by 092 and yet to write one. An unfiltered
 * read returns whichever job ran last, which here is a coin toss between the
 * drain and the nightly tick — so the note would switch itself off because an
 * unrelated job ran, which is worse than a hardcoded note because it looks
 * derived. PipelineOverview.tsx carries the same constant for the same reason
 * and records the afternoon that mistake made its page contradict itself.
 *
 * The filter is applied HERE as well as in the caller's query, so a caller
 * that forgets it still gets an answer about the right job. planRules.ts
 * makes the same choice for nurture-tick.
 */
export const DRAIN_JOB = "drain-outbound";

/**
 * How long the sender may be silent before the silence is the story.
 *
 * SIX HOURS, DERIVED FROM WHAT THE JOB ACTUALLY DOES RATHER THAN FROM ITS
 * CRON. `.github/workflows/outbound.yml` asks for a run every five minutes,
 * which promises 288 a day; GitHub delivers about eight. Every run writes a
 * row here — started before the work, finished either way — so the intervals
 * between those rows are the real cadence. Measured over all 19 rows, from
 * 2026-09-02 18:39 to 2026-09-05 01:10 UTC:
 *
 *     18 intervals · mean 3h 02m · median ~2h 34m · WORST 4h 44m
 *
 * crm#12 measured that same worst gap independently, from the workflow run
 * list rather than from this table. A threshold taken from the five-minute
 * cron would fire within the hour, every hour, and an alarm that is always on
 * is not an alarm. Six hours clears the worst observed gap by about 27% and
 * still catches a sender that has genuinely stopped, which is the only state
 * worth a red sentence.
 *
 * THIS IS AN OBSERVATION, NOT A GUARANTEE, and it is the number in this file
 * most likely to be wrong later. If crm#12 is fixed and the drain starts
 * keeping its schedule, six hours is far too slack and should come down hard;
 * if GitHub gets later still, it will cry wolf. Change it here.
 */
export const DRAIN_STALE_HOURS = 6;

export type DrainState = {
  kind: "unknown" | "never" | "healthy" | "stale" | "unhealthy";
  /** The newest drain-outbound row, or null when there has never been one. */
  lastRunAt: string | null;
  /** Whole hours since that run STARTED. Null when there is no run at all. */
  hoursSince: number | null;
  tone: WaitTone;
  /**
   * What the screen says, or NULL when there is nothing to say.
   *
   * A healthy drainer gets no sentence, and that is the entire point of this
   * function: the note takes itself down. What stood here before was
   * `NOTHING_DRAINS_YET`, a hardcoded paragraph asserting that drain-outbound
   * was not deployed. It was deployed on 2026-09-02 and had run 19 times by
   * 2026-09-04, so the screen spent two days telling every reader that email
   * could not be sent while it was being sent. A comment saying "delete this
   * the day they ship" is not a mechanism; nothing fails when it is forgotten,
   * and nothing did. See atomic#10.
   */
  note: string | null;
};

/**
 * Whether the sender is alive, from the sender's own record.
 *
 * THE THIRD STATE IS THE ONE WORTH HAVING. "Never run" and "running" a
 * hardcoded note can at least approximate, one of them by luck. "It ran, and
 * then it stopped" is the state a person actually needs to be told about, it
 * is the state this queue was built to make visible, and no constant can ever
 * express it.
 *
 * `runs === null` MEANS THE READ FAILED, and is not the same as `[]`. An
 * empty array is a successful read of a table with no drain rows in it — the
 * sender has never run. A failed read tells us nothing at all, and this
 * screen does not let a failed read print reassurance: emptyQueueNote() takes
 * a null policy for exactly the same reason. Note that planRules'
 * describeNurtureRun() does NOT draw this distinction; it treats a missing
 * list as no runs. Different function, deliberate difference.
 */
export function describeDrain(
  runs: readonly DrainRun[] | null | undefined,
  now: Date = new Date(),
): DrainState {
  if (runs == null)
    return {
      kind: "unknown",
      lastRunAt: null,
      hoursSince: null,
      tone: "alarm",
      note: "The sender's own record could not be read, so this page cannot say whether anything is going out. A quiet queue under an unreadable pipeline_runs is not evidence that email is flowing — it is an absence of evidence either way.",
    };

  const last = runs
    .filter((r) => r.job === DRAIN_JOB)
    .sort((a, b) => (a.started_at < b.started_at ? 1 : -1))[0];

  if (!last)
    return {
      kind: "never",
      lastRunAt: null,
      hoursSince: null,
      tone: "alarm",
      note: "Nothing has ever been sent from here. drain-outbound has no recorded run at all, and it is the only thing that sends — so a message written now would sit at Due for ever. Every control on this page is live against the database; the sender is what is missing.",
    };

  const hoursSince = Math.floor(
    (now.getTime() - new Date(last.started_at).getTime()) / 3_600_000,
  );
  const when =
    hoursSince <= 0
      ? "less than an hour ago"
      : hoursSince === 1
        ? "an hour ago"
        : hoursSince < 48
          ? `${hoursSince} hours ago`
          : `${Math.floor(hoursSince / 24)} days ago`;
  const because = last.detail ? ` (${last.detail})` : "";

  if (hoursSince >= DRAIN_STALE_HOURS)
    return {
      kind: "stale",
      lastRunAt: last.started_at,
      hoursSince,
      tone: "alarm",
      note: `The sender has stopped. drain-outbound last ran ${when}${because}, and it normally runs several times a day — so anything in this queue is sitting here rather than going out, and nothing below will move until it runs again.`,
    };

  // `running` is the row this run inserted before doing the work, so a recent
  // one means the drain is in flight, not that it failed.
  if (last.status !== "ok" && last.status !== "running")
    return {
      kind: "unhealthy",
      lastRunAt: last.started_at,
      hoursSince,
      tone: "alarm",
      note: `The sender is running but not succeeding. Its last run — ${when} — reported “${last.status}”${because}. Some or all of what it tried to send did not go.`,
    };

  return {
    kind: "healthy",
    lastRunAt: last.started_at,
    hoursSince,
    tone: "normal",
    note: null,
  };
}

/**
 * The screen's read of `pipeline_runs`, turned into a state — pending, failed
 * or answered, in one place that can be argued with in Node.
 *
 * THE PENDING CASE IS THE REASON THIS EXISTS. `describeDrain([])` says the
 * sender has never run, which is right for an empty table and badly wrong for
 * a read that has not come back yet: it would put a red paragraph claiming
 * email is dead on the first paint of every visit, then take it away. That is
 * an absence assertion made before the evidence arrives, which is the same
 * mistake the browser suite's own header warns about. Left as a ternary in
 * the component it was a branch no test could reach; here it is three lines
 * and three assertions.
 */
export function drainNoteFor(
  query: {
    isPending: boolean;
    error: unknown;
    rows: readonly DrainRun[] | undefined;
  },
  now?: Date,
): DrainState | null {
  if (query.isPending) return null;
  return describeDrain(query.error ? null : (query.rows ?? []), now);
}

/* ── the hold is a floor, not a schedule ─────────────────────── */

/**
 * How many drain runs before this file will describe a cadence at all.
 *
 * Four runs is three intervals, which is the fewest that can show a spread
 * rather than a single number that happens to be a coincidence. Below it the
 * sentence says so and asserts nothing — an honest "we do not know yet" is
 * the only safe reading, because every wrong answer here is wrong in the
 * reassuring direction. See holdSentence().
 */
export const MIN_CADENCE_RUNS = 4;

/** What `pipeline_runs` says about how often the sender actually starts. */
export type DrainCadence = {
  /** Drain runs the measurement is drawn from. Always >= MIN_CADENCE_RUNS. */
  runs: number;
  /** MEDIAN gap between consecutive starts, in minutes. */
  typicalMinutes: number;
  /**
   * Longest stretch with no run, in minutes — INCLUDING THE ONE OPEN NOW.
   *
   * The open stretch is why this takes a clock. Completed gaps alone would
   * let a sender that died two hours past its worst previous gap still print
   * that previous gap as the ceiling, which is the reassuring-direction lie
   * this whole function exists to stop.
   */
  worstMinutes: number;
};

/**
 * The sender's real cadence, from the sender's own record.
 *
 * PURE, AND IT TAKES THE ROWS RATHER THAN FETCHING THEM — same contract as
 * describeDrain(), and for the same reason: the sentence a person reads
 * before deciding whether they can still unsend should be arguable in Node in
 * under a second, not behind a browser and a credential.
 *
 * THE `job` FILTER IS APPLIED HERE TOO. `pipeline_runs` is shared by five
 * jobs — counted 2026-09-05: nurture-tick 25, drain-outbound 20, ttb-refresh
 * 5, nurture-digest 2, and advance-enrollments, which 092 added and which has
 * written no row yet. nurture-tick alone has more rows than the drain and
 * runs on a schedule of its own, so mixing them would produce a confident
 * cadence for a job nobody asked about.
 */
export function drainCadence(
  runs: readonly DrainRun[] | null | undefined,
  now: Date = new Date(),
): DrainCadence | null {
  if (runs == null) return null;

  const starts = runs
    .filter((r) => r.job === DRAIN_JOB)
    .map((r) => new Date(r.started_at).getTime())
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => a - b);
  if (starts.length < MIN_CADENCE_RUNS) return null;

  const gaps: number[] = [];
  for (let i = 1; i < starts.length; i += 1)
    gaps.push((starts[i] - starts[i - 1]) / 60_000);

  // MEDIAN, not mean. The distribution is skewed by long stalls, and a mean
  // dragged up by one four-hour gap would describe no run that ever happened.
  const sorted = [...gaps].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const typicalMinutes =
    sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;

  const openStretch = (now.getTime() - starts[starts.length - 1]) / 60_000;

  return {
    runs: starts.length,
    typicalMinutes,
    worstMinutes: Math.max(...gaps, openStretch),
  };
}

/** A whole number of minutes, in words. Hours once it is past one. */
function inWords(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  if (total < 60) return total === 1 ? "1 minute" : `${total} minutes`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  const hoursPart = hours === 1 ? "1 hour" : `${hours} hours`;
  if (rest === 0) return hoursPart;
  return `${hoursPart} ${rest} ${rest === 1 ? "minute" : "minutes"}`;
}

/**
 * What the hold actually promises.
 *
 * THE SENTENCE THIS SCREEN IS MOST LIKELY TO GET WRONG, AND IT HAS BEEN
 * WRONG. What stood here read the cron and promised that a message
 * "typically leaves 5–20 minutes after it is written", because
 * `.github/workflows/outbound.yml` asks for a run every five minutes.
 * GitHub delivers about eight runs a day. Measured over every drain-outbound
 * row on 2026-09-05 — 19 intervals — the median gap was 2h 41m and the worst
 * 4h 44m, so the screen was out by an order of magnitude and out in the
 * direction that matters: somebody told they have twenty minutes to recall a
 * message may believe it is still recallable hours after it became eligible
 * to go. atomic#16; crm#12 is the scheduler half.
 *
 * SO IT REPORTS, IT DOES NOT PROMISE. Every number in the sentence is
 * measured from the rows this screen already fetches, which means it follows
 * the sender: if crm#12 is fixed and the drain starts keeping its schedule,
 * the sentence says so on the next render with nothing to remember. Replacing
 * "5–20 minutes" with a hardcoded "2–5 hours" would have reproduced the exact
 * defect — a promise that was true when written and has nothing to make it
 * fail when it stops being true. That is the failure atomic#10 fixed one
 * function down, in NOTHING_DRAINS_YET.
 *
 * WITH TOO FEW RUNS IT CLAIMS NOTHING. A cadence from one interval is a
 * coincidence, and a wrong window here is worse than no window: the floor is
 * the only thing the hold actually guarantees, and that half is true whatever
 * the sender is doing.
 *
 * Worded as a minimum throughout, for the reason it always was: "sends in 5
 * minutes" would be wrong most of the time, and wrong in the direction that
 * makes a working system look broken.
 */
export function holdSentence(
  holdMinutes: number,
  runs: readonly DrainRun[] | null | undefined,
  now: Date = new Date(),
): string {
  const cadence = drainCadence(runs, now);
  const measured = cadence
    ? `across its last ${cadence.runs} runs the gap between starts has been about ${inWords(cadence.typicalMinutes)} typically, and the longest quiet stretch, counting the one open right now, is ${inWords(cadence.worstMinutes)}`
    : null;

  if (holdMinutes <= 0)
    return measured
      ? `The hold is set to zero, so there is no unsend window at all: a message is eligible the moment it is written and the sender's next run may take it. Nothing can be cancelled once that happens. There is usually a delay before that run: ${measured}. That delay is luck, not a control.`
      : "The hold is set to zero, so there is no unsend window at all: a message is eligible the moment it is written and the sender's next run may take it. Nothing can be cancelled once that happens. There may be a delay before that run, but there are too few recorded runs to say how long — and any such delay would be luck, not a control.";

  const unit = holdMinutes === 1 ? "minute" : "minutes";
  const floor = `Every message waits at least ${holdMinutes} ${unit} before it may be sent. That is the unsend window, and it is a floor rather than a schedule: when the hold expires the message waits again, for the sender's next run.`;

  return measured
    ? `${floor} The sender's own record is the only evidence for how long that second wait is — ${measured}. Those are measurements of what has happened, not a promise about what will.`
    : `${floor} There are too few recorded runs to say how long that second wait has been, so this page does not guess; it reports the sender's health separately.`;
}

/* ── a stop states its reason ──────────────────────────────────────────── */

export type ReasonCheck =
  | { ok: true; reason: string }
  | { ok: false; message: string };

function requireReason(
  raw: string | null | undefined,
  message: string,
): ReasonCheck {
  const reason = (raw ?? "").trim();
  return reason ? { ok: true, reason } : { ok: false, message };
}

/**
 * Why is outbound email being paused?
 *
 * REFUSED HERE AS WELL AS BY POSTGRES. `outbound_policy_pause_has_reason` —
 * CHECK (paused = false OR pause_reason IS NOT NULL) — makes an unaccountable
 * pause unstorable, but the point is not to pass the check. A constraint
 * violation arrives as a toast nobody can act on, and the reason has to exist
 * for the NEXT person: they find outbound email off, and the only way to
 * decide whether resuming is safe is to know what stopped it.
 *
 * Third time this house rule appears — ending an enrollment, cancelling a
 * message, pausing the system — which makes it a rule rather than a
 * coincidence.
 */
export function validatePauseReason(raw: string | null | undefined) {
  return requireReason(
    raw,
    "Say why outbound email is being paused. The next person to open this page finds every message stopped, and the only way for them to judge whether resuming is safe is to know what stopped it.",
  );
}

/**
 * Why is this message being cancelled?
 *
 * cancel_outbound() raises on a blank or whitespace reason. Refusing before
 * the round trip means the sentence lands beside the field that caused it,
 * rather than as a Postgres error in a toast that is gone before anyone
 * re-reads the box.
 */
export function validateCancelReason(raw: string | null | undefined) {
  return requireReason(
    raw,
    "Say why this is being cancelled. A message that never went out leaves no other trace, so without the reason nobody can later tell a considered stop from a mistake.",
  );
}

/* ── the writes ────────────────────────────────────────────────────────── */

/**
 * Every patch stamps `updated_at`, because NOTHING ELSE DOES.
 *
 * Verified against production rather than assumed: `outbound_policy` has no
 * triggers at all, and `updated_at` is only a column DEFAULT — which fires on
 * insert and never again. Without this the row would go on claiming it had
 * not changed since migration 067 ran, and "when was outbound email paused?"
 * would have no answer anywhere in the system.
 */
const stamped = <T extends object>(patch: T, now: Date) => ({
  ...patch,
  updated_at: now.toISOString(),
});

/**
 * Pause, with the reason in the same patch.
 *
 * BOTH COLUMNS MOVE TOGETHER because the CHECK constraint says they must: a
 * patch of `{paused: true}` alone is refused outright by Postgres.
 */
export function pausePatch(reason: string, now: Date = new Date()) {
  return stamped({ paused: true as const, pause_reason: reason }, now);
}

/**
 * Resume, and CLEAR the reason.
 *
 * The constraint permits a stale reason to sit on a running system —
 * `paused = false OR pause_reason IS NOT NULL` is satisfied either way — and
 * the settings panel would then print last week's outage beside a queue that
 * is draining normally. There is no pause history table to preserve it for,
 * so leaving it behind buys nothing and costs a false sentence.
 */
export function resumePatch(now: Date = new Date()) {
  return stamped({ paused: false as const, pause_reason: null }, now);
}

/**
 * Clear the brake — BOTH halves.
 *
 * `outbound_policy_trip_has_reason` allows `tripped_at` alone to be cleared,
 * leaving the reason behind, and the next trip would then be read alongside
 * the previous trip's numbers. Those numbers are the entire content of that
 * message: how many became due, and against which limit.
 */
export function clearTripPatch(now: Date = new Date()) {
  return stamped({ tripped_at: null, tripped_reason: null }, now);
}

/* ── the settings ──────────────────────────────────────────────────────── */

export type SettingsKey = "hold_minutes" | "max_per_run" | "max_per_hour";

/**
 * SMALLINT, all three. Postgres refuses 40000 with a range error that names
 * no column, so the bound is held here where it can be explained.
 */
const SMALLINT_MAX = 32767;

/**
 * The three adjustable settings, each with the one line that says what it
 * does. ops#8 asks for "sensible settings we can adjust"; a number with no
 * sentence beside it is a number nobody dares adjust.
 */
export const SETTINGS_FIELDS = [
  {
    key: "hold_minutes" as const,
    label: "Hold",
    unit: "minutes",
    /** `hold_minutes >= 0` — zero is legal, and turns the window off. */
    min: 0,
    what: "How long a message waits before it may be sent. This is the unsend window, and the only period in which anything on this page can be cancelled.",
  },
  {
    key: "max_per_run" as const,
    label: "Stop above",
    unit: "due at once",
    /** `max_per_run > 0`. */
    min: 1,
    what: "More than this many messages due in one run is treated as a malfunction rather than a workload: the brake trips and nothing at all is sent.",
  },
  {
    key: "max_per_hour" as const,
    label: "Stop above",
    unit: "per hour",
    /** `max_per_hour > 0`. */
    min: 1,
    what: "Sent in the last hour plus the number now due. The slower brake, for a bug that dribbles rather than bursts — nine every five minutes never trips the limit above.",
  },
];

export type SettingsDraft = Record<SettingsKey, string>;

export type SettingsCheck =
  | { ok: true; patch: Record<SettingsKey, number> & { updated_at: string } }
  | { ok: false; errors: Partial<Record<SettingsKey, string>> };

/**
 * Are these three numbers storable, and what do they mean if not?
 *
 * HELD ON THIS SIDE OF THE WIRE AS WELL AS IN POSTGRES, and the two failures
 * are not equivalent. `max_per_run = 0` is refused by a CHECK constraint with
 * a message naming the constraint; `hold_minutes = 2.5` is NOT refused at all
 * — smallint truncates it to 2, so a person who set a two-and-a-half-minute
 * unsend window silently gets two. That is a small lie about the one number
 * on this page people will check against a clock.
 */
export function validateSettings(
  draft: SettingsDraft,
  now: Date = new Date(),
): SettingsCheck {
  const errors: Partial<Record<SettingsKey, string>> = {};
  const values = {} as Record<SettingsKey, number>;

  for (const field of SETTINGS_FIELDS) {
    const raw = (draft[field.key] ?? "").trim();
    if (!raw) {
      errors[field.key] = "Give a number — this setting has no blank meaning.";
      continue;
    }
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      errors[field.key] = `"${raw}" is not a number.`;
      continue;
    }
    if (!Number.isInteger(value)) {
      errors[field.key] =
        "Whole numbers only. The column is a smallint, so a fraction is truncated rather than refused, and the setting would quietly become something you did not choose.";
      continue;
    }
    if (value < field.min) {
      errors[field.key] =
        field.min === 0
          ? "Cannot be negative — the database enforces hold_minutes >= 0."
          : `Must be at least 1: the database enforces ${field.key} > 0, and zero would mean stopping on every message.`;
      continue;
    }
    if (value > SMALLINT_MAX) {
      errors[field.key] =
        `Too large. The column is a smallint, so ${SMALLINT_MAX} is the ceiling.`;
      continue;
    }
    values[field.key] = value;
  }

  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, patch: stamped(values, now) };
}

/* ── cancelling ────────────────────────────────────────────────────────── */

/**
 * The one call this screen makes outside the data provider.
 *
 * `cancel_outbound` is security definer and staff-gated inside, so it has to
 * go through the Supabase client rather than PostgREST's table endpoints.
 * Injected as a parameter for the reason sendBatches() injects its `call`:
 * the guard being tested is the one that decides whether email is stopped,
 * and a rule that important should be provable without a network, a
 * credential, or a browser.
 */
export type CancelRpc = (args: {
  p_ids: number[] | null;
  p_reason: string;
}) => Promise<{ data: unknown; error: { message: string } | null }>;

/** The real one. Lazily imported so no test that never cancels needs a client. */
export const cancelViaSupabase: CancelRpc = async (args) => {
  const { getSupabaseClient } = await import("../providers/supabase/supabase");
  return getSupabaseClient().rpc("cancel_outbound", args);
};

/**
 * Cancel some queued messages, or all of them.
 *
 * `ids === null` MEANS EVERYTHING, and that is the function's own contract:
 * `p_ids is null or e.id = any (p_ids)`. An empty array is NOT the same
 * thing — it would match nothing, return 0, and read on screen exactly like
 * "there was nothing to cancel", so the two are kept distinct by the type.
 *
 * Returns the count the FUNCTION reports, never the count we asked for. See
 * cancelOutcome() for why that difference matters.
 */
export async function cancelOutbound(
  rpc: CancelRpc,
  ids: number[] | null,
  rawReason: string | null | undefined,
): Promise<number> {
  const check = validateCancelReason(rawReason);
  if (!check.ok) throw new Error(check.message);

  const { data, error } = await rpc({ p_ids: ids, p_reason: check.reason });
  // The function's own refusals — "only staff may cancel outbound email", the
  // blank-reason raise — arrive here. Reporting them verbatim is the whole
  // point: a generic "could not cancel" would send somebody looking at the
  // network tab for a permissions problem the database already named.
  if (error) throw new Error(error.message);
  return Number(data ?? 0);
}

/**
 * What actually happened, said out loud.
 *
 * THE SHORTFALL IS THE INTERESTING NUMBER. cancel_outbound() will not touch a
 * row with `claimed_at` set, because that message may already be at Resend
 * and recording it as cancelled would make the log lie about something that
 * went out. So "cancel all" over a queue of five with two in flight returns
 * three — and a screen that said "cancelled" and stopped would leave somebody
 * believing two messages had been stopped that are on their way to inboxes.
 */
export function cancelOutcome(requested: number, cancelled: number): string {
  if (cancelled === 0)
    return requested === 1
      ? "Nothing was cancelled. The message had already been claimed by the drainer, so it may already be at Resend and cannot be recorded as cancelled."
      : "Nothing was cancelled. Every message here had already been claimed by the drainer, so they may already be at Resend and cannot be recorded as cancelled.";

  const noun = cancelled === 1 ? "message" : "messages";
  const missed = requested - cancelled;
  if (missed <= 0) return `Cancelled ${cancelled} ${noun}.`;

  return `Cancelled ${cancelled} ${noun}. The other ${missed} had already been handed to the sender and cannot be cancelled — they may already be at Resend, and marking them cancelled would make the record deny a message that went out.`;
}

/* ── the brake, and what resuming will actually do ─────────────────────── */

/**
 * The three ways `outbound_queue` spells "past its send_after and unclaimed".
 *
 * TAKEN FROM wait_state, NOT RECOMPUTED. These three are exactly
 * claim_outbound_batch's own `v_due` predicate —
 *
 *     status = 'queued' and cancelled_at is null
 *       and claimed_at is null and send_after <= now()
 *
 * — split by whichever brake happens to be holding them. Re-deriving the same
 * predicate here from `send_after` and a browser clock is how this screen and
 * the drainer come to disagree about whether the brake is about to trip.
 */
export const DUE_WAIT_STATES: readonly string[] = ["due", "paused", "stopped"];

/** The messages the next drainer run would count. */
export function dueNow(rows: readonly OutboundQueueRow[]): OutboundQueueRow[] {
  return rows.filter((row) => DUE_WAIT_STATES.includes(row.wait_state));
}

/**
 * Will clearing the trip simply trip it again?
 *
 * RESUMING DOES NOT DRAIN THE QUEUE — it only lets the next run look at it,
 * and that run applies the same brake to the same rows. Clearing a trip while
 * fourteen messages are still due trips it again within five minutes, and
 * from the screen that reads as Resume having done nothing at all. The two
 * real options are to raise the limit or to cancel some of the queue, so the
 * sentence names both.
 */
export function wouldTripAgain(
  policy: OutboundPolicy,
  dueCount: number,
): string | null {
  if (dueCount <= policy.max_per_run) return null;
  return `${dueCount} messages are still due, against a limit of ${policy.max_per_run}. Resuming will not send them: the next run applies the same brake to the same rows and trips again within minutes. Cancel some of the queue, or raise the limit, first.`;
}

/* ── what the screen says when there is nothing to show ────────────────── */

export type QueueNote = {
  headline: string;
  detail: string;
  tone: WaitTone;
};

/**
 * An empty queue, explained.
 *
 * "NOTHING QUEUED" AND "NOTHING DRAINING" ARE OPPOSITE FACTS. An empty queue
 * is the normal, healthy state of this system and must read that way — but a
 * queue that is empty while the brake is on is a system that has stopped, and
 * a shared "No messages" renders the two identically. That is the same defect
 * FollowUps.tsx writes three separate empty sentences to avoid, and it is
 * worse here: there, a wrong empty state hides neglect; here it hides a
 * failure to send anything at all.
 *
 * A POLICY WE COULD NOT READ IS NOT A POLICY THAT SAYS ALL IS WELL. Every
 * other branch is a confident claim about the state of the system, and a
 * failed read must not be able to print one.
 */
export function emptyQueueNote(policy: OutboundPolicy | null): QueueNote {
  if (!policy)
    return {
      headline: "Nothing is queued, and we cannot say whether that is good.",
      tone: "alarm",
      detail:
        "The policy row could not be read, so we do not know whether outbound email is paused, whether the brake has tripped, or what the hold is. An empty list under an unknown policy is not evidence that anything is working.",
    };

  if (policy.tripped_at)
    return {
      headline: "Nothing is queued — but the brake is still on.",
      tone: "alarm",
      detail:
        "The queue is empty, so there is nothing waiting. That is not the same as working: the brake tripped and has not been cleared, so nothing would drain even if something were queued. Clear the trip above.",
    };

  if (policy.paused)
    return {
      headline: "Nothing is queued — and outbound email is paused.",
      tone: "attention",
      detail:
        "The queue is empty, so nothing is being held back right now. But outbound email is still paused: anything written from here on would sit in this list until somebody resumes.",
    };

  return {
    headline: "Nothing is waiting to go out.",
    tone: "normal",
    detail:
      "This is the normal, healthy state — an empty outbound queue means every message that was written has been dealt with, not that anything is broken. Messages appear here during their hold window, which is the only time they can be cancelled.",
  };
}
